import React, { useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import { STANDE } from "../../standeTokens";

/** Turns a run of points into a smooth path rather than a chain of corners. */
export const toPath = (points) => {
  if (!points.length) return "";
  if (points.length < 3) {
    return points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  }
  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const mid = { x: (points[i].x + points[i + 1].x) / 2, y: (points[i].y + points[i + 1].y) / 2 };
    d += ` Q${points[i].x},${points[i].y} ${mid.x},${mid.y}`;
  }
  const last = points[points.length - 1];
  return `${d} L${last.x},${last.y}`;
};

/** Slop before a press on a line becomes a drag rather than a click. */
const DRAG_SLOP = 3;

/**
 * Every freehand stroke on the board, in one SVG beneath the items.
 *
 * The layer itself ignores the pointer so clicks reach the canvas. Under the
 * cursor each stroke gets a wide invisible twin that does take it, so a line
 * can be picked up like anything else: click to select it, drag to move it,
 * Delete to remove it. The selected line is shown with a soft blue halo.
 *
 * A drag moves the line with a transform and writes the new points once, on
 * release, so moving a long line is one change to undo and one message to
 * the room.
 */
const InkLayer = ({
  strokes,
  liveStroke,
  remoteStrokes = [],
  selectable = false,
  selectedId = null,
  scale = 1,
  onSelect,
  onMoveEnd,
}) => {
  const [drag, setDrag] = useState(null);
  const gesture = useRef(null);

  const startDrag = (e, stroke) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    onSelect?.(stroke.id);
    gesture.current = { id: stroke.id, px: e.clientX, py: e.clientY, moved: false };
    const move = (ev) => {
      const g = gesture.current;
      if (!g) return;
      const dx = (ev.clientX - g.px) / scale;
      const dy = (ev.clientY - g.py) / scale;
      if (!g.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
      g.moved = true;
      setDrag({ id: g.id, dx, dy });
    };
    const up = (ev) => {
      const g = gesture.current;
      gesture.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setDrag(null);
      if (!g?.moved) return;
      const dx = (ev.clientX - g.px) / scale;
      const dy = (ev.clientY - g.py) / scale;
      const moved = strokes.find((s) => s.id === g.id);
      if (moved) {
        onMoveEnd?.(g.id, moved.points.map((p) => ({ ...p, x: p.x + dx, y: p.y + dy })));
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <Box position="absolute" inset="0" pointerEvents="none">
      <svg width="100%" height="100%" style={{ display: "block", overflow: "visible" }}>
        {strokes.map((s) => {
          const d = toPath(s.points);
          const shift = drag && drag.id === s.id ? `translate(${drag.dx} ${drag.dy})` : undefined;
          const isSelected = selectable && selectedId === s.id;
          return (
            <g key={s.id} transform={shift}>
              {isSelected && (
                <path
                  d={d}
                  fill="none"
                  stroke={STANDE.primaryBlue}
                  strokeOpacity={0.35}
                  strokeWidth={s.width + 8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
              <path
                d={d}
                fill="none"
                stroke={s.colour}
                strokeWidth={s.width}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {selectable && (
                // What the pointer actually lands on: wider than the ink, so
                // a thin line is not a pixel-perfect target.
                <path
                  data-board-item
                  data-ink={s.id}
                  d={d}
                  fill="none"
                  stroke="transparent"
                  strokeWidth={Math.max(16, s.width + 12)}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ pointerEvents: "stroke", cursor: drag ? "grabbing" : "pointer" }}
                  onPointerDown={(e) => startDrag(e, s)}
                />
              )}
            </g>
          );
        })}
        {remoteStrokes.map((s) =>
          s?.points?.length > 1 ? (
            <path
              key={`remote-${s.id}`}
              d={toPath(s.points)}
              fill="none"
              stroke={s.colour}
              strokeWidth={s.width}
              strokeLinecap="round"
              strokeLinejoin="round"
              // Slightly soft until it lands as a real stroke, so it reads as
              // something still being drawn.
              opacity={0.85}
            />
          ) : null
        )}
        {liveStroke && liveStroke.points.length > 1 && (
          <path
            d={toPath(liveStroke.points)}
            fill="none"
            stroke={liveStroke.colour}
            strokeWidth={liveStroke.width}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </Box>
  );
};

export default InkLayer;
